/*
 * The device list query: the filters the screen holds, and how they become
 * the `/v2/conf` query string. Shared by the list and by the Excel export
 * so both ask the API for exactly the same fleet.
 */
import { DeviceCreate } from '../../../api/models';
import config from '../../../config/env';
import { safeStorage } from '../../../utils/safeStorage';

export interface DeviceFilters {
  /** Client ids; the backend parses the comma list into an IN clause. */
  clients: string[];
  /** Hardware type codes. */
  deviceTypes: string[];
  /** Template ids; '0' means "no template". */
  templates: string[];
  /** '' (all), 'active' or 'refresh'. */
  status: string;
  search: string;
  includeInactive: boolean;
}

export type DeviceSortField = keyof DeviceCreate;

/** The columns the backend can order by; anything else falls back to the IMEI. */
const SERVER_SORT_FIELDS = new Set(['idDevice', 'dateTime', 'lastUpdateDateTime', 'deviceType', 'idClient', 'needsRefresh']);

export interface DeviceQueryOptions {
  page: number;
  pageSize: number;
  sortField: DeviceSortField;
  sortDirection: 'asc' | 'desc';
}

export const buildDeviceQuery = (filters: DeviceFilters, options: DeviceQueryOptions): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('page', String(options.page));
  params.set('page_size', String(options.pageSize));
  params.set('full_data', 'true');
  params.set('slim', 'true');
  if (filters.clients.length) params.set('client_id', filters.clients.join(','));
  if (filters.deviceTypes.length) params.set('device_type', filters.deviceTypes.join(','));
  if (filters.templates.length) params.set('template_id', filters.templates.join(','));
  if (filters.status === 'refresh') params.set('needs_refresh', 'true');
  else if (filters.status === 'active') params.set('needs_refresh', 'false');
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.includeInactive) params.set('include_inactive', 'true');
  params.set('sort_by', SERVER_SORT_FIELDS.has(options.sortField as string) ? (options.sortField as string) : 'idDevice');
  params.set('sort_dir', options.sortDirection);
  return params;
};

export interface DevicePage {
  items: DeviceCreate[];
  /** Null when the API answered with a bare array (older deployments). */
  pagination: { page: number; page_size: number; total_items: number; total_pages: number; has_next: boolean; has_previous: boolean } | null;
}

/** The API row with the snake_case configuration keys mirrored in camelCase. */
const mapDeviceRow = (device: any): DeviceCreate => ({
  ...device,
  mqttHost: device.mqtt_host,
  mqttPort: device.mqtt_port,
  mqttUser: device.mqtt_user,
  mqttPass: device.mqtt_pass,
  syslogHost: device.syslog_host,
  syslogPort: device.syslog_port,
  syslogFacility: device.syslog_facility,
  keepaliveTimer: device.keepalive_timer,
  dateTime: device.dateTime || device.date_time,
});

/** One page of devices from `/v2/conf`. */
export const fetchDevicePage = async (filters: DeviceFilters, options: DeviceQueryOptions, signal?: AbortSignal): Promise<DevicePage> => {
  const token = safeStorage.getItem('access_token');
  const params = buildDeviceQuery(filters, options);
  const response = await fetch(`${config.api.baseUrl}/v2/conf?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    signal,
  });
  if (!response.ok) throw new Error(`Failed to fetch devices: ${response.statusText}`);
  const data = await response.json();
  if (data && data.items && data.pagination) {
    return { items: data.items.map(mapDeviceRow), pagination: data.pagination };
  }
  if (Array.isArray(data)) return { items: data, pagination: null };
  if (config.dev.debug) console.warn('[DEVICES DEBUG] Unexpected response format:', data);
  return { items: [], pagination: null };
};
